import { Component, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  @Input() showTitle = true;
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private router = inject(Router);

  error = signal<string | null>(null);
  info = signal<string | null>(null);
  form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]]
  });

  async submit() {
    this.error.set(null);
    this.info.set(null);
    if (this.form.invalid) return;
    const { email, password } = this.form.getRawValue();
    try {
      const res = await this.auth.login(email, password);
      if (res && typeof (res as any).challenge === 'string' && (res as any).challenge === 'NEW_PASSWORD_REQUIRED') {
        const session = (res as any).session;
        await this.router.navigate(['/new-password'], { queryParams: { email, session } });
        return;
      }
      await this.router.navigateByUrl('/');
    } catch (e: any) {
      if (e?.status === 0) {
        this.error.set('The server cannot be reached. Please check your network connection and try again.');
        return;
      }
      const code = e?.error?.error;
      if (code === 'NotAuthorizedException') {
        this.error.set('Username/password did not match.');
        return;
      } else if (code === 'UserNotFoundException') {
        this.error.set('No account found for this email.');
        return;
      } else if (code === 'TooManyRequestsException' || code === 'LimitExceededException') {
        this.error.set('Too many attempts. Please wait a moment and try again.');
        return;
      }
      this.error.set(e?.error?.message || 'Login failed');
    }
  }

  submitFromEnter(event: Event) {
    event.preventDefault();
    this.submit();
  }
}
